import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import { getActiveSchemeClass } from '../utils/themeUtils';
import useBackdropClose from '../utils/useBackdropClose';

export default function InfoCard({
  id,
  icon,
  title,
  count,
  subtitle,
  summary,
  defaultOpen = false,
  defaultExpanded,
  open: controlledOpen,
  expanded: controlledExpanded,
  onOpenChange,
  onExpandedChange,
  className = '',
  headerActions,
  children,
  scrollable = false
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen || defaultExpanded || false);
  const isControlled = controlledOpen !== undefined || controlledExpanded !== undefined;
  const open = isControlled ? (controlledOpen ?? controlledExpanded) : internalOpen;

  const setOpen = useCallback((next) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
    onExpandedChange?.(next);
  }, [isControlled, onOpenChange, onExpandedChange]);

  const close = useCallback(() => setOpen(false), [setOpen]);
  const backdrop = useBackdropClose(close);

  const onHeaderClick = (e) => {
    if (e.target.closest('.info-card-actions')) return;
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  const themeClass = getActiveSchemeClass();

  const modal = open ? createPortal(
    <div
      className={`modal-backdrop modal-large info-card-modal-backdrop ${themeClass}`.trim()}
      data-card-id={id || undefined}
      {...backdrop}
      role="presentation"
    >
      <div className="modal info-card-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content info-card-modal-shell">
          <button
            type="button"
            className="info-card-modal-close"
            onClick={close}
            aria-label="Close"
          >
            <CloseIcon />
          </button>

          <div className="info-card-modal-header">
            {icon && <div className="info-card-leading">{icon}</div>}
            <div className="info-card-meta">
              <div className="info-card-title-row">
                <span className="info-card-title">{title}</span>
                {count != null && <span className="info-card-count">{count}</span>}
              </div>
              {subtitle && <span className="info-card-subtitle">{subtitle}</span>}
            </div>
          </div>

          <div className={`info-card-content card-content ${scrollable ? 'scrollable' : ''}`.trim()}>
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <section
        id={id}
        className={`info-card info-card-collapsible ${className}`.trim()}
      >
        <button
          type="button"
          className="info-card-toggle"
          onClick={onHeaderClick}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          {icon && <div className="info-card-leading">{icon}</div>}

          <div className="info-card-meta">
            <div className="info-card-title-row">
              <span className="info-card-title">{title}</span>
            </div>
            {subtitle && <span className="info-card-subtitle">{subtitle}</span>}
            {summary && <span className="info-card-summary">{summary}</span>}
          </div>

          {headerActions && (
            <div className="info-card-actions" onClick={(e) => e.stopPropagation()}>
              {headerActions}
            </div>
          )}

          {count != null && <span className="info-card-count">{count}</span>}
        </button>
      </section>

      {modal}
    </>
  );
}
